import { ApiError, Badge, EmptyState, ErrorState, Field, Loading, Options, formatDate } from "@corecrm/ui";
import type { ReactNode } from "react";
import { useState } from "react";
import { useBoard, useMe, usePlanning } from "../api/queries";
import type { Project, TaskCard } from "../api/types";
import { PRIORITY, SIZE_LABEL, points } from "../lib/agile";
import { Status } from "./bits";
import "./sprints.css";

/* The read-only sprint Plan and work Board a project shows its client when the team shares its
   sprints (docs/agile-tasks.md §7): the staff layout without moves, edits or dependency details. */

/** One task, as both views show it. */
export function SprintTaskCard({ task: t }: { task: TaskCard }) {
  const [priority, kind] = PRIORITY[t.priority];
  return (
    <article className="kcard ro" aria-label={`${t.key}: ${t.title}`}>
      <div className="kc-top">
        <span className="mono kc-key">{t.key}</span>
        {t.kind === "bug" && <Badge kind="bad">Bug</Badge>}
        {t.blocked && <Badge kind="bad">Blocked</Badge>}
      </div>
      <div className="kc-t">{t.title}</div>
      {t.epic && <div className="kc-s">{t.epic.name}</div>}
      <div className="kc-m">
        <span className="kc-badges">
          {t.size && <Badge>{`${SIZE_LABEL[t.size]} · ${points(t.points)}`}</Badge>}
          <Badge kind={kind}>{priority}</Badge>
        </span>
        <span className="kc-s">{t.stage.name}</span>
      </div>
      {(t.due_date || t.assignee) && (
        <div className="kc-m kc-s">
          {t.due_date && <span>Due {formatDate(t.due_date)}</span>}
          {t.assignee && <span>{t.assignee.name}</span>}
        </div>
      )}
    </article>
  );
}

function Column({ title, meta, children }: { title: string; meta?: ReactNode; children: ReactNode }) {
  return (
    <section className="kcol" aria-label={title}>
      <div className="kcol-h">
        <h3>{title}</h3>
      </div>
      {meta && <div className="kcol-meta">{meta}</div>}
      {children}
    </section>
  );
}

function Cards({ tasks, empty }: { tasks: TaskCard[]; empty?: string }) {
  if (!tasks.length) return empty ? <p className="faint kcol-empty">{empty}</p> : null;
  return (
    <>
      {tasks.map((t) => (
        <SprintTaskCard key={t.id} task={t} />
      ))}
    </>
  );
}

/** A 404 means the team stopped sharing its sprints since the project loaded. */
function BoardError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  if (error instanceof ApiError && error.status === 404)
    return <EmptyState>The team isn't sharing its sprint plan on this project.</EmptyState>;
  return <ErrorState error={error} onRetry={onRetry} />;
}

const tasks = (n: number) => `${n} task${n === 1 ? "" : "s"}`;

const showBugsKey = (userId: string) => `corecrm.portal.show-bugs.${userId}`;

/** "Show bugs" on the Plan and Board (UX-195): off until you turn it on, then remembered for you
    on this browser. Storage can be unavailable (private windows): it just isn't remembered. */
export function useShowBugs(): [boolean, (on: boolean) => void] {
  const userId = useMe().data?.id ?? "";
  const [picked, setPicked] = useState<boolean | null>(null);
  let stored = false;
  try {
    stored = !!userId && localStorage.getItem(showBugsKey(userId)) === "1";
  } catch {
    stored = false;
  }
  const set = (on: boolean) => {
    setPicked(on);
    try {
      if (userId) localStorage.setItem(showBugsKey(userId), on ? "1" : "0");
    } catch {
      // not remembered
    }
  };
  return [picked ?? stored, set];
}

function ShowBugs({
  project,
  on,
  onChange,
}: {
  project: Project;
  on: boolean;
  onChange: (on: boolean) => void;
}) {
  if (!project.can_view_bugs) return null;
  return (
    <label className="check">
      <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} />
      <span>Show bugs</span>
    </label>
  );
}

const dates = (start: string, end: string) => `${formatDate(start)} – ${formatDate(end)}`;

/** Sprints for a phase, each with its tasks and the tasks linked to it by due date, then the backlog. */
export function PlanView({ project: p }: { project: Project }) {
  const [phaseId, setPhaseId] = useState<string | null>(null);
  const [showBugs, setShowBugs] = useShowBugs();
  const bugs = showBugs && p.can_view_bugs;
  const plan = usePlanning(p.id, phaseId, bugs);
  const current = phaseId ?? plan.data?.phase_id ?? p.phases[0]?.id ?? "";

  return (
    <>
      {(p.phases.length > 1 || p.can_view_bugs) && (
        <div className="sprint-bar">
          {p.phases.length > 1 && (
            <Field label="Phase">
              <select value={current} onChange={(e) => setPhaseId(e.target.value)}>
                <Options list={p.phases.map((ph) => [ph.id, ph.name])} />
              </select>
            </Field>
          )}
          <ShowBugs project={p} on={showBugs} onChange={setShowBugs} />
        </div>
      )}
      {plan.isPending ? (
        <Loading />
      ) : plan.isError ? (
        <BoardError error={plan.error} onRetry={() => plan.refetch()} />
      ) : (
        <div className="kanban sprint-board" aria-label="Sprint plan">
          {plan.data.sprints.map((s) => (
            <Column
              key={s.id}
              title={s.display_name}
              meta={
                <>
                  <span>
                    <Status status={s.status} /> <span className="mono">{points(s.points)}</span>
                  </span>
                  <span>{dates(s.start_date, s.end_date)}</span>
                  {s.goal && <span>{s.goal}</span>}
                </>
              }
            >
              <Cards tasks={s.tasks} empty="No tasks in this sprint." />
              {s.linked.length > 0 && (
                <>
                  <h4 className="kcol-sub">Linked by due date</h4>
                  <Cards tasks={s.linked} />
                </>
              )}
            </Column>
          ))}
          <Column
            title="Backlog"
            meta={<span>{tasks(plan.data.backlog.undated.length + plan.data.backlog.dated.length)}</span>}
          >
            <Cards
              tasks={plan.data.backlog.undated}
              empty={plan.data.backlog.dated.length ? undefined : "Nothing in the backlog."}
            />
            {plan.data.backlog.dated.length > 0 && (
              <>
                <h4 className="kcol-sub">Due outside a sprint</h4>
                <Cards tasks={plan.data.backlog.dated} />
              </>
            )}
          </Column>
        </div>
      )}
    </>
  );
}

/** The active sprint's tasks by stage. */
export function BoardView({ project: p }: { project: Project }) {
  const [showBugs, setShowBugs] = useShowBugs();
  const board = useBoard(p.id, showBugs && p.can_view_bugs);
  if (board.isPending) return <Loading />;
  if (board.isError) return <BoardError error={board.error} onRetry={() => board.refetch()} />;
  const { sprint, stages } = board.data;
  if (!sprint) return <EmptyState>No sprint is under way right now.</EmptyState>;
  return (
    <>
      <div className="sprint-bar">
        <h2>{sprint.display_name}</h2>
        <span className="faint">
          {dates(sprint.start_date, sprint.end_date)} · <span className="mono">{points(sprint.points)}</span>
        </span>
        {sprint.goal && <span>{sprint.goal}</span>}
        <ShowBugs project={p} on={showBugs} onChange={setShowBugs} />
      </div>
      <div className="kanban sprint-board" aria-label="Work board">
        {stages.map((st) => (
          <Column key={st.stage.id} title={st.stage.name} meta={<span>{tasks(st.tasks.length)}</span>}>
            <Cards tasks={st.tasks} empty="No tasks." />
          </Column>
        ))}
      </div>
    </>
  );
}
